import {
  getDownloadUrlForDocument,
  useDeleteUserDocument,
  useUserFiles,
} from "@coaching-culture/api-client";
import {
  Button,
  Circle,
  DatePill,
  Flex,
  IconButton,
  LinkButton,
  Loader,
  Panel,
  Table,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { ScrollableContainer } from "components/ScrollableContainer";
import { Toggle } from "components/Toggle";
import { useState } from "react";
import { FaDownload, FaFile, FaFileUpload, FaTrash } from "react-icons/fa";
import styled from "styled-components";
import { grid, GridProps } from "styled-system";
import { EditDocumentModal } from "./EditDocumentModal";
import { UploadDocumentModal } from "./UploadDocumentModal";

const Widget = styled(Panel)<GridProps>`
  ${grid};
`;

export type DocumentsWidgetProps = {
  userId: string;
  conversationId?: string;
};

export const DocumentsWidget = ({
  userId,
  conversationId,
}: DocumentsWidgetProps) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [allDocs, setAllDocs] = useState(false);
  const [editing, setEditing] = useState<null | string>(null);
  const [me] = useUser();

  const files = useUserFiles(userId);
  const deleteMut = useDeleteUserDocument();

  const download = async (fileId: string) => {
    const url = await getDownloadUrlForDocument(userId, fileId);

    window.location.replace(url);
  };

  const deleteItem = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      await deleteMut.mutateAsync({ userId, id });
    }
  };

  let data = files.data ?? [];

  if (conversationId != null && !allDocs) {
    data = data.filter((x) => x.conversation?.id === conversationId);
  }

  return (
    <Widget width={"100%"}>
      {editing && (
        <EditDocumentModal
          userId={userId}
          documentId={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {showUploadModal && (
        <UploadDocumentModal
          userId={userId}
          conversationId={conversationId}
          onClose={() => setShowUploadModal(false)}
        />
      )}
      <Flex borderBottom={1} height={83} justifyContent={"space-between"} p={4}>
        <Flex alignItems="center">
          <Circle icon={FaFile} size={"medium"} color="primary" mr={2} />
          <Text
            fontSize={4}
            fontWeight={600}
            lineHeight={1}
            mr={1}
            color="black"
            as="h1"
          >
            User Documents
          </Text>
        </Flex>
        <Flex alignItems="center" justifyContent="center" flexDirection="row">
          {conversationId != null && (
            <Toggle
              onChange={setAllDocs}
              value={allDocs}
              leftLabel={"This Conversation"}
              rightLabel={"All"}
            />
          )}
          <Button
            ml={2}
            onClick={() => setShowUploadModal(true)}
            color="primary"
            icon={FaFileUpload}
          >
            Upload
          </Button>
        </Flex>
      </Flex>
      {files.data == null ? (
        <Loader />
      ) : (
        <ScrollableContainer maxHeight={250}>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Uploader</th>
                <th>Uploaded On</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {files.data.filter((x) => x.deletedOn == null).length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <Text
                      fontWeight={600}
                      color="grey3"
                      textAlign={"center"}
                      py={1}
                    >
                      No Files
                    </Text>
                  </td>
                </tr>
              )}
              {data
                .filter((x) => x.deletedOn == null)
                .map((x) => (
                  <tr key={x.id}>
                    <td>
                      <Flex alignItems={"center"}>
                        <Circle icon={FaFile} mr={2} />
                        <LinkButton onClick={() => setEditing(x.id)}>
                          {x.name}
                        </LinkButton>
                      </Flex>
                    </td>
                    <td>
                      <UserFlag user={x.uploader} />
                    </td>
                    <td>
                      <DatePill value={x.uploadedAt} dateOnly />
                    </td>
                    <td>
                      <Flex justifyContent={"flex-end"} style={{ gap: 6 }}>
                        <IconButton
                          icon={FaDownload}
                          color="positive"
                          onClick={() => download(x.id)}
                        />
                        {me.id === x.uploader.id && (
                          <IconButton
                            icon={FaTrash}
                            color="danger"
                            disabled={deleteMut.isLoading}
                            onClick={() => deleteItem(x.id)}
                          />
                        )}
                      </Flex>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </ScrollableContainer>
      )}
    </Widget>
  );
};
